import React, { useContext, useEffect, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, Form, Row, Col,
    FormGroup, Label, Input, Button
} from 'reactstrap';
import Select from 'react-select';
import { AuthContext } from '../../context/AuthContext';
import { ContaProvisionadaContext } from '../../context/ContaProvisionadaContext';
import { ContaFinanceiraContext } from '../../context/ContaFinanceiraContext';
import { CategoriaFinanceiraContext } from '../../context/CategoriaFinanceiraContext';
import { FormaPagamentoContext } from '../../context/FormaPagamentoContext';
import { CreateContaProvisionadaType } from '../../context/hooks/useContaProvisionada';
import { TipoContaPagarContext } from '../../context/TipoContaPagarContext';
import { CurrencyInput } from '../Form/CurrencyInput';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    onCreated?: () => void;
}

export const CreateContaProvisionadaModal: React.FC<Props> = ({ isOpen, toggle, onCreated }) => {
    const { objUserTk } = useContext(AuthContext);
    const { createContaProvisionada } = useContext(ContaProvisionadaContext);
    const { handleGetContaFinanceiraByEmpresaId, listContaFinanceiraByEmpresa } = useContext(ContaFinanceiraContext);
    const { handleGetCategoriaFinanceiraByEmpresaId, listCategoriaFinanceiraByEmpresa } = useContext(CategoriaFinanceiraContext);
    const { handleGetFormaPagamentoByEmpresaId, listFormaPagamentoByEmpresa } = useContext(FormaPagamentoContext);
    const { handleGetTipoContaPagarByEmpresaId, listTipoContaPagarByEmpresa } = useContext(TipoContaPagarContext);

    const [form, setForm] = useState<CreateContaProvisionadaType>({
        valor: 0,
        taxa: 0,
        dataInicial: '',
        documento: '',
        codigoTransacao: 0,
        historico: '',
        bandeira: '',
        recorrencia: 0,
        empresaId: objUserTk.EmpresaId,
        contaFinanceiraId: 0,
        categoriaFinanceiraId: 0,
        formaPagamentoId: 0,
        tipoContaPagarId: 0
    });

    useEffect(() => {
        if (!listContaFinanceiraByEmpresa.length) handleGetContaFinanceiraByEmpresaId(objUserTk.EmpresaId);
        if (!listCategoriaFinanceiraByEmpresa.length) handleGetCategoriaFinanceiraByEmpresaId(objUserTk.EmpresaId);
        if (!listFormaPagamentoByEmpresa.length) handleGetFormaPagamentoByEmpresaId(objUserTk.EmpresaId);
        if (!listTipoContaPagarByEmpresa.length) handleGetTipoContaPagarByEmpresaId(objUserTk.EmpresaId);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setForm({
                valor: 0,
                taxa: 0,
                dataInicial: '',
                documento: '',
                codigoTransacao: 0,
                historico: '',
                bandeira: '',
                recorrencia: 0,
                empresaId: objUserTk.EmpresaId,
                contaFinanceiraId: 0,
                categoriaFinanceiraId: 0,
                formaPagamentoId: 0,
                tipoContaPagarId: 0
            });
        }
    }, [isOpen]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const ok = await createContaProvisionada({
            ...form,
            valor: +form.valor,
            taxa: +form.taxa,
            codigoTransacao: +form.codigoTransacao
        });
        if (ok) {
            onCreated?.();
            toggle();
        }
    };

    const contaOpts = listContaFinanceiraByEmpresa.map(c => ({ value: c.id, label: c.name }));
    const catOpts = listCategoriaFinanceiraByEmpresa.map(c => ({ value: c.id, label: c.name }));
    const formaOpts = listFormaPagamentoByEmpresa.map(f => ({ value: f.id, label: f.name }));
    const tipoOpts = listTipoContaPagarByEmpresa.map(tp => ({ value: tp.id, label: tp.name }));

    return (
        <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="lg">
            <ModalHeader toggle={toggle}>Adicionar nova conta</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Valor</Label>
                                <CurrencyInput
                                    name="valor"
                                    value={form.valor}
                                    onValueChange={v => setForm(prev => ({ ...prev, valor: v }))}
                                    placeholder="R$ 0,00"
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Taxa</Label>
                                <CurrencyInput
                                    name="taxa"
                                    value={form.taxa}
                                    onValueChange={v => setForm(prev => ({ ...prev, taxa: v }))}
                                    placeholder="R$ 0,00"
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Data Inicial</Label>
                                <Input className="form-control-alternative" type="date" name="dataInicial"
                                    value={form.dataInicial} onChange={onChange} required />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Documento</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="documento"
                                    value={form.documento}
                                    onChange={onChange}
                                    placeholder="Número do documento"
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label">Recorrencia</Label>
                                <Input
                                    type="select"
                                    name="recorrencia"
                                    className="form-control-alternative"
                                    value={form.recorrencia}
                                    onChange={(e) => setForm(prev => ({ ...prev, recorrencia: +e.target.value }))}
                                >
                                    <option value={""}>Selecione uma opção</option>
                                    <option value={0}>Diário</option>
                                    <option value={1}>Semanal</option>
                                    <option value={2}>Mensal</option>
                                    <option value={3}>Anual</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Código Transação</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    name="codigoTransacao"
                                    value={form.codigoTransacao}
                                    onChange={onChange}
                                    placeholder="Código da transação"
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Histórico</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="historico"
                                    value={form.historico}
                                    onChange={onChange}
                                    placeholder="Histórico da transação"
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Bandeira</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="bandeira"
                                    value={form.bandeira}
                                    onChange={onChange}
                                    placeholder="Bandeira do cartão ou meio de pagamento"
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Conta Financeira</Label>
                                <Select
                                    className="form-control-alternative"
                                    options={contaOpts}
                                    value={contaOpts.find(f => f.value === form.contaFinanceiraId)}
                                    onChange={opt => setForm(prev => ({ ...prev, contaFinanceiraId: opt ? opt.value : 0 }))}
                                    isClearable
                                    classNamePrefix="react-select"
                                    placeholder="Selecione uma conta"
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Categoria Financeira</Label>
                                <Select
                                    className="form-control-alternative"
                                    options={catOpts}
                                    value={catOpts.find(f => f.value === form.categoriaFinanceiraId)}
                                    onChange={opt => setForm(prev => ({ ...prev, categoriaFinanceiraId: opt ? opt.value : 0 }))}
                                    isClearable
                                    classNamePrefix="react-select"
                                    placeholder="Selecione uma categoria"
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Forma de Pagamento</Label>
                                <Select
                                    className="form-control-alternative"
                                    options={formaOpts}
                                    value={formaOpts.find(f => f.value === form.formaPagamentoId)}
                                    onChange={opt => setForm(prev => ({ ...prev, formaPagamentoId: opt ? opt.value : 0 }))}
                                    isClearable
                                    classNamePrefix="react-select"
                                    placeholder="Selecione uma forma de pagamento"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Tipo de Conta Pagar</Label>
                                <Select
                                    className="form-control-alternative"
                                    options={tipoOpts}
                                    value={tipoOpts.find(f => f.value === form.tipoContaPagarId)}
                                    onChange={opt => setForm(prev => ({ ...prev, tipoContaPagarId: opt ? opt.value : 0 }))}
                                    isClearable
                                    classNamePrefix="react-select"
                                    placeholder="Selecione um tipo de conta"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-3">
                        <Button style={{ background: '#585858', color: '#fff' }} type="submit">Criar conta</Button>
                        <Button style={{ background: '#585858', color: '#fff' }} className="ml-2" onClick={toggle}>Cancelar</Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};
export default CreateContaProvisionadaModal;